import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button } from '@saleshandy/design-system';
import { ChevronDown, ChevronUp, InfoCircle } from '@saleshandy/icons';
import { OverlayTooltip, Placement } from '../../../components/overlay';
import RadioButtonGroup from '../../../components/radio-button-group';

export interface LeadSelectionBannerProps {
  /** Count currently shown in the badge (respects limit / select-all display) */
  selectedCount: number;
  /** Number of rows on the current page */
  pageCount: number;
  /** Total rows across all pages */
  totalCount: number;
  /** Hard cap from credits / plan (e.g. remaining reveal credits) */
  maxCustomLimit?: number | null;
  /** Per-tab bulk-action cap (e.g. Saved Search 25k / People 10k) */
  bulkSelectionLimit?: number | null;
  /** Currently-applied custom limit (from Redux) */
  appliedLimit: number | null;
  /** True when select-all-across-pages flag is active */
  isSelectAllAcrossPages: boolean;

  currentPageSelectedCount?: number | null;
  /** Callback: select current page rows only */
  onApplyCurrentPage: () => void;
  /** Callback: apply a custom N as the selection count */
  onApplyCustomLimit: (value: number) => void;
  /** Callback: "Select all leads" link — omit to hide the link */
  onSelectAll?: () => void;
  /** Callback: "Clear Selection" link */
  onClearSelection?: () => void;
  /** Singular item label, e.g. "lead" or "company" */
  itemName?: string;

  freePlanSelectionLimit?: number | null;

  freePlanLimitMessage?: string | null;
  /** Value of the "limit" filter (limit per company), used for the K+ display threshold in formatCount */
  limitPerCompanyFilter?: string | null;
}

type SelectionMode = 'current_page' | 'custom_number';

interface SelectionModeDropdownProps {
  selectionMode?: SelectionMode;
  radioOptions: Array<{ value: SelectionMode; label: React.ReactNode }>;
  onRadioChange: (val: SelectionMode) => void;
  customInputValue: string;
  onCustomInputChange: (val: string) => void;
  onCustomKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onApplyCustom: () => void;
  isValidCustom: boolean;
  showCustomError: boolean;
  customErrorText: string;
  totalCountLabel: string;
}

// Memoized dropdown panel. It deliberately receives NO selection-count props,
// so toggling row selection (which only changes the pill count in the parent)
// skips this entire subtree — RadioButtonGroup, OverlayTooltip and Button are
// not re-rendered on every checkbox click. It only re-renders when something it
// actually shows changes (selection mode, input value, validation state).
const SelectionModeDropdown: React.FC<SelectionModeDropdownProps> = React.memo(
  ({
    selectionMode,
    radioOptions,
    onRadioChange,
    customInputValue,
    onCustomInputChange,
    onCustomKeyDown,
    onApplyCustom,
    isValidCustom,
    showCustomError,
    customErrorText,
    totalCountLabel,
  }) => (
    <div className="bs-lead-selection-banner__dropdown">
      <RadioButtonGroup
        name="lsb-selection-mode"
        value={selectionMode}
        onChange={onRadioChange}
        variant={RadioButtonGroup.Variant.Default}
        className="bs-lead-selection-banner__rb-group"
        options={radioOptions}
      />
      {selectionMode === 'custom_number' && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          className={`bs-lead-selection-banner__custom-input-row${
            showCustomError
              ? ' bs-lead-selection-banner__custom-input-row--has-error'
              : ''
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <span className="bs-lead-selection-banner__first-label">First</span>
          {/* eslint-disable jsx-a11y/no-autofocus */}
          <input
            autoFocus
            type="number"
            value={customInputValue}
            onChange={(e) => onCustomInputChange(e.target.value)}
            onKeyDown={onCustomKeyDown}
            className="bs-lead-selection-banner__custom-input"
          />
          {/* eslint-enable jsx-a11y/no-autofocus */}
          <span className="bs-lead-selection-banner__custom-input-total">
            of {totalCountLabel}
          </span>
          <Button
            onClick={onApplyCustom}
            disabled={!isValidCustom}
            className="bs-lead-selection-banner__apply-btn"
          >
            Apply
          </Button>
        </div>
      )}

      {selectionMode === 'custom_number' && showCustomError && (
        <span className="bs-lead-selection-banner__custom-error regular-1 red-txt-12">
          {customErrorText}
        </span>
      )}
    </div>
  ),
);

const LeadSelectionBanner: React.FC<LeadSelectionBannerProps> = ({
  selectedCount,
  pageCount,
  totalCount,
  maxCustomLimit = null,
  bulkSelectionLimit = null,
  appliedLimit,
  isSelectAllAcrossPages,
  onApplyCurrentPage,
  onApplyCustomLimit,
  onSelectAll,
  onClearSelection,
  itemName = 'lead',
  currentPageSelectedCount = null,
  freePlanSelectionLimit = null,
  freePlanLimitMessage = null,
  limitPerCompanyFilter = null,
}) => {
  // The banner mounts fresh the moment a selection appears (it is unmounted at
  // zero selections). Clicking the header "select all current page" checkbox
  // from a zero-selection state selects the whole page AND mounts the banner in
  // the same tick — too late for a DOM click listener to catch. So we decide the
  // initial open state here: open if the banner was born with the whole current
  // page already selected; otherwise (single/partial selection) stay closed.
  const [isDropdownOpen, setIsDropdownOpen] = useState(() => {
    const allPageSelectedOnMount =
      currentPageSelectedCount != null
        ? currentPageSelectedCount > 0 && currentPageSelectedCount === pageCount
        : selectedCount > 0 && selectedCount === pageCount;
    return isSelectAllAcrossPages || allPageSelectedOnMount;
  });
  const [selectionMode, setSelectionMode] = useState<SelectionMode>();
  const [customInputValue, setCustomInputValue] = useState<string>('');

  // Ref on the pill wrapper — dropdown is anchored to it
  const pillWrapperRef = useRef<HTMLDivElement>(null);
  // Ref on the entire banner — used for outside-click detection
  const bannerRef = useRef<HTMLDivElement>(null);


  const wasPageFullySelectedRef = useRef(
    isSelectAllAcrossPages ||
      (currentPageSelectedCount != null
        ? currentPageSelectedCount > 0 && currentPageSelectedCount === pageCount
        : selectedCount > 0 && selectedCount === pageCount),
  );
  useEffect(() => {
    // Select-all-across-pages keeps every page "full"; opening on each page
    // change would be noise. That flow opens the dropdown via its own handler.
    if (isSelectAllAcrossPages) {
      wasPageFullySelectedRef.current = true;
      return;
    }
    const isPageFullySelected =
      currentPageSelectedCount != null
        ? currentPageSelectedCount > 0 && currentPageSelectedCount === pageCount
        : selectedCount > 0 && selectedCount === pageCount;
    if (isPageFullySelected && !wasPageFullySelectedRef.current) {
      setIsDropdownOpen(true);
    }
    wasPageFullySelectedRef.current = isPageFullySelected;
  }, [
    currentPageSelectedCount,
    pageCount,
    selectedCount,
    isSelectAllAcrossPages,
  ]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!isDropdownOpen) return undefined;
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as Element | null;
      const isCheckboxClick = !!target?.closest?.(
        '.tanstack-table input[type="checkbox"]',
      );
      if (isCheckboxClick) {
        return;
      }
      if (bannerRef.current && !bannerRef.current.contains(target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    // Small delay prevents the same click that opened the dropdown from closing it
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleOutsideClick);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isDropdownOpen]);

  // Pre-fill custom input whenever dropdown opens
  useEffect(() => {
    if (isDropdownOpen) {
      setCustomInputValue(
        String(selectedCount > 0 ? selectedCount : pageCount),
      );
      const allCurrentPageSelected =
        currentPageSelectedCount != null
          ? currentPageSelectedCount > 0 &&
            currentPageSelectedCount === pageCount
          : selectedCount > 0 && selectedCount === pageCount;
      setSelectionMode(
        // eslint-disable-next-line no-nested-ternary
        isSelectAllAcrossPages && appliedLimit != null
          ? 'custom_number'
          : allCurrentPageSelected
          ? 'current_page'
          : undefined,
      );
    }
  }, [isDropdownOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isDropdownOpen) return;

    if (isSelectAllAcrossPages && appliedLimit != null) {
      if (selectionMode !== 'custom_number') setSelectionMode('custom_number');
      return;
    }

    if (selectionMode === 'custom_number') return;

    // Use currentPageSelectedCount when available for accurate detection.
    const shouldBeCurrentPage =
      currentPageSelectedCount != null
        ? currentPageSelectedCount > 0 && currentPageSelectedCount === pageCount
        : selectedCount > 0 && selectedCount === pageCount;
    if (shouldBeCurrentPage) {
      if (selectionMode !== 'current_page') setSelectionMode('current_page');
    } else if (selectionMode === 'current_page') {
      setSelectionMode(undefined);
    }
  }, [
    isDropdownOpen,
    selectedCount,
    pageCount,
    currentPageSelectedCount,
    isSelectAllAcrossPages,
    appliedLimit,
    selectionMode,
  ]);

  const toggleDropdown = () => setIsDropdownOpen((prev) => !prev);

  const handleSelectAll = () => {
    onSelectAll?.();
    setIsDropdownOpen(false);
  };

  const effectiveMax = useMemo(() => {
    const caps = [totalCount];
    if (bulkSelectionLimit != null && bulkSelectionLimit > 0) {
      caps.push(bulkSelectionLimit);
    }
    if (maxCustomLimit != null && maxCustomLimit > 0) {
      caps.push(maxCustomLimit);
    }
    if (freePlanSelectionLimit != null && freePlanSelectionLimit > 0) {
      caps.push(freePlanSelectionLimit);
    }
    return Math.min(...caps);
  }, [totalCount, bulkSelectionLimit, maxCustomLimit, freePlanSelectionLimit]);

  // ── Custom number validation ──────────────────────────────────────────
  const trimmed = customInputValue.trim();
  const isInteger = /^\d+$/.test(trimmed);
  const parsed = isInteger ? Number(trimmed) : NaN;
  const isValidCustom = isInteger && parsed >= 1 && parsed <= effectiveMax;
  const showCustomError = trimmed.length > 0 && !isValidCustom;

  let customErrorText = '';
  if (showCustomError) {
    if (!isInteger) {
      customErrorText = 'Enter a whole number';
    } else if (parsed < 1) {
      customErrorText = 'Enter a number greater than 0';
    } else {
      // Free plan page cap is the tightest constraint (check first).
      const isFreePlanLimited =
        freePlanSelectionLimit != null &&
        freePlanSelectionLimit > 0 &&
        effectiveMax === freePlanSelectionLimit;

      const isCreditLimited =
        !isFreePlanLimited &&
        maxCustomLimit != null &&
        maxCustomLimit > 0 &&
        effectiveMax === maxCustomLimit &&
        maxCustomLimit < totalCount &&
        (bulkSelectionLimit == null || maxCustomLimit < bulkSelectionLimit);

      if (isFreePlanLimited && freePlanLimitMessage) {
        customErrorText = freePlanLimitMessage;
      } else if (isCreditLimited) {
        customErrorText = `Selection limited to ${effectiveMax.toLocaleString()} records based on available credits`;
      } else {
        const isCompanyTab = itemName.toLowerCase() === 'company';
        customErrorText = isCompanyTab
          ? `Maximum ${effectiveMax.toLocaleString()} companies per selection`
          : `Maximum ${effectiveMax.toLocaleString()} leads per selection`;
      }
    }
  }

  // Latest-value refs so the dropdown handlers below stay referentially stable
  // across selections. Putting `selectedCount` / `parsed` / `isValidCustom`
  // directly in useCallback deps would give the handlers a new identity on
  // every checkbox click, which would defeat SelectionModeDropdown's React.memo.
  const selectedCountRef = useRef(selectedCount);
  selectedCountRef.current = selectedCount;
  const parsedRef = useRef(parsed);
  parsedRef.current = parsed;
  const isValidCustomRef = useRef(isValidCustom);
  isValidCustomRef.current = isValidCustom;

  const handleCurrentPageClick = useCallback(() => {
    setSelectionMode('current_page');
    onApplyCurrentPage();
    setIsDropdownOpen(false);
  }, [onApplyCurrentPage]);

  const handleCustomNumberRadioClick = useCallback(() => {
    setSelectionMode('custom_number');
    if (selectedCountRef.current > 0) {
      setCustomInputValue(String(selectedCountRef.current));
    }
  }, []);

  const handleApplyCustom = useCallback(() => {
    if (!isValidCustomRef.current) return;
    onApplyCustomLimit(parsedRef.current);
    setIsDropdownOpen(false);
  }, [onApplyCustomLimit]);

  const handleCustomInputChange = useCallback(
    (val: string | React.ChangeEvent<HTMLInputElement>) => {
      const next = typeof val === 'string' ? val : val?.target?.value ?? '';
      setCustomInputValue(next);
    },
    [],
  );

  const handleCustomKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (['e', 'E', '+', '-', '.'].includes(e.key)) {
        e.preventDefault();
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        handleApplyCustom();
      }
    },
    [handleApplyCustom],
  );

  // ── Radio change bridge ───────────────────────────────────────────────
  const handleRadioChange = useCallback(
    (val: SelectionMode) => {
      if (val === 'current_page') {
        handleCurrentPageClick();
      } else {
        handleCustomNumberRadioClick();
      }
    },
    [handleCurrentPageClick, handleCustomNumberRadioClick],
  );

  const formatCount = (count?: number): string => {
    if (count === undefined || count === null) {
      return '';
    }
    if (limitPerCompanyFilter) {
      const limitValue = Number(limitPerCompanyFilter);
      if (!Number.isNaN(limitValue) && limitValue > 0) {
        if (limitValue * 10000 === count) {
          return `${parseFloat((count / 1000).toFixed(1))}K+`;
        }
      }
    }
    if (count >= 1000000) {
      return `${parseFloat((count / 1000000).toFixed(1))}M`;
    }
    if (count === 10000) {
      return '10K+';
    }
    if (count >= 1000) {
      return `${parseFloat((count / 1000).toFixed(1))}K`;
    }
    return count.toLocaleString('en-US');
  };

  // ── Display label helpers ─────────────────────────────────────────────
  const pluralize = (word: string, count: number) => {
    if (count === 1) return word;
    if (word.toLowerCase() === 'company') {
      return word[0] === 'C' ? 'Companies' : 'companies';
    }
    const endsWithY = /[^aeiou]y$/i.test(word);
    if (endsWithY) return `${word.slice(0, -1)}ies`;
    const endsWithSibilant = /(s|x|z|ch|sh)$/i.test(word);
    if (endsWithSibilant) return `${word}es`;
    return `${word}s`;
  };

  const displayLabel = pluralize(itemName, selectedCount);
  const pageLabel = pluralize(itemName, pageCount);

  // ── Dropdown radio options ────────────────────────────────────────────
  // Memoized so the (static) option JSX isn't rebuilt — and the non-memoized
  // RadioButtonGroup isn't forced to re-render — on every selection-count tick.
  const radioOptions = useMemo(
    () => [
      {
        value: 'current_page' as SelectionMode,
        label: (
          <div className="bs-lead-selection-banner__radio-content">
            <div className="bs-lead-selection-banner__radio-page-row">
              <span className="bs-lead-selection-banner__radio-label">
                Select from current page
              </span>
              <span className="bs-lead-selection-banner__radio-sublabel">
                {pageCount.toLocaleString()} {pageLabel}
              </span>
            </div>
          </div>
        ),
      },
      {
        value: 'custom_number' as SelectionMode,
        label: (
          <div className="bs-lead-selection-banner__radio-content">
            <span className="bs-lead-selection-banner__radio-label regular-2 gray-txt-9 d-flex align-items-center gap-4">
              Custom number
              <OverlayTooltip
                placement={Placement.Bottom}
                text="Selection starts from the first record in your current results"
              >
                <InfoCircle
                  width={14}
                  height={14}
                  className="bs-lead-selection-banner__info-icon"
                />
              </OverlayTooltip>
            </span>
          </div>
        ),
      },
    ],
    [pageCount, pageLabel],
  );

  return (
    <div className="bs-lead-selection-banner" ref={bannerRef}>
      {/* ── Pill wrapper (dropdown anchored here) ── */}
      <div
        className="bs-lead-selection-banner__pill-wrapper"
        ref={pillWrapperRef}
      >
        <span
          className="bs-lead-selection-banner__pill"
          role="button"
          tabIndex={0}
          onClick={toggleDropdown}
          onKeyDown={(e) => e.key === 'Enter' && toggleDropdown()}
          aria-haspopup="listbox"
          aria-expanded={isDropdownOpen}
        >
          <span className="bs-lead-selection-banner__pill-count">
            {selectedCount.toLocaleString()} {displayLabel}
          </span>
          {isDropdownOpen ? (
            <ChevronUp width={12} height={12} />
          ) : (
            <ChevronDown width={12} height={12} />
          )}
        </span>

        {/* ── Dropdown — anchored to pill-wrapper ── */}
        {isDropdownOpen && (
          <SelectionModeDropdown
            selectionMode={selectionMode}
            radioOptions={radioOptions}
            onRadioChange={handleRadioChange}
            customInputValue={customInputValue}
            onCustomInputChange={handleCustomInputChange}
            onCustomKeyDown={handleCustomKeyDown}
            onApplyCustom={handleApplyCustom}
            isValidCustom={isValidCustom}
            showCustomError={showCustomError}
            customErrorText={customErrorText}
            totalCountLabel={formatCount(totalCount)}
          />
        )}
      </div>

      {/* ── "selected." plain text ── */}
      <span className="bs-lead-selection-banner__selected-text">selected.</span>

      {/* ── Right-side links ── */}
      <div className="bs-lead-selection-banner__links">
        {/* Leading divider — separates "selected." from the action links */}
        {(onSelectAll || onClearSelection) && (
          <span className="alert-divider-vertical" />
        )}
        {onSelectAll && (
          <>
            <Button
              variant="link"
              onClick={handleSelectAll}
              className="bs-lead-selection-banner__link-btn p-0"
            >
              Select all {pluralize(itemName, 2)}
            </Button>
            <span className="alert-divider-vertical" />
          </>
        )}
        {onClearSelection && (
          <Button
            variant="link"
            onClick={onClearSelection}
            className="bs-lead-selection-banner__link-btn p-0"
          >
            Clear Selection
          </Button>
        )}
      </div>
    </div>
  );
};

// Memoized so the banner (and its non-memoized RadioButtonGroup dropdown) only
// re-renders when its own props change — not on every unrelated TanStackTable
// render (column resize, width recalcs, hover state, etc.).
export default React.memo(LeadSelectionBanner);
